import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const GlobalRiskControls = () => {
  const [showEmergencyDialog, setShowEmergencyDialog] = useState(false);
  const [emergencyConfirmation, setEmergencyConfirmation] = useState('');
  const [riskLimits, setRiskLimits] = useState({
    maxExposure: 500000,
    marginCallLevel: 100,
    stopOutLevel: 50,
    maxDrawdown: 15,
    dailyLossLimit: 25000
  });

  const [isEditingLimits, setIsEditingLimits] = useState(false);
  const [tempLimits, setTempLimits] = useState(riskLimits);

  // Mock current status
  const currentStatus = {
    totalExposure: 387500,
    marginLevel: 156.8,
    currentDrawdown: 8.2,
    dailyPnL: -12450,
    openPositions: 23,
    connectionStatus: 'connected'
  };

  const handleEmergencyClose = () => {
    if (emergencyConfirmation?.toLowerCase() === 'close all') {
      console.log('Emergency close all positions executed');
      setShowEmergencyDialog(false);
      setEmergencyConfirmation('');
      // In a real app, this would trigger the emergency close
    }
  };

  const handleSaveLimits = () => {
    setRiskLimits(tempLimits);
    setIsEditingLimits(false);
    console.log('Risk limits updated:', tempLimits);
  };

  const handleCancelEdit = () => {
    setTempLimits(riskLimits);
    setIsEditingLimits(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const getStatusColor = (current, limit, isReverse = false) => {
    const ratio = current / limit;
    if (isReverse) {
      if (ratio <= 0.5) return 'text-red-600 dark:text-red-400';
      if (ratio <= 0.8) return 'text-amber-600 dark:text-amber-400';
      return 'text-green-600 dark:text-green-400';
    } else {
      if (ratio >= 0.9) return 'text-red-600 dark:text-red-400';
      if (ratio >= 0.7) return 'text-amber-600 dark:text-amber-400';
      return 'text-green-600 dark:text-green-400';
    }
  };

  const getProgressColor = (current, limit, isReverse = false) => {
    const ratio = current / limit;
    if (isReverse) {
      if (ratio <= 0.5) return 'bg-red-500';
      if (ratio <= 0.8) return 'bg-amber-500';
      return 'bg-green-500';
    } else {
      if (ratio >= 0.9) return 'bg-red-500';
      if (ratio >= 0.7) return 'bg-amber-500';
      return 'bg-green-500';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Global Risk Controls</h2>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className={`flex items-center space-x-2 px-3 py-2 rounded-md ${
            currentStatus?.connectionStatus === 'connected' ?'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300' :'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              currentStatus?.connectionStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'
            } ${currentStatus?.connectionStatus === 'connected' ? 'animate-pulse' : ''}`}></div>
            <span className="text-xs font-medium">
              {currentStatus?.connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
      </div>
      {/* Risk Limits Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Risk Limits</h3>
            {!isEditingLimits ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditingLimits(true)}
                iconName="Edit"
                iconPosition="left"
              >
                Edit Limits
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveLimits}
                  iconName="Check"
                  iconPosition="left"
                >
                  Save
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-3">
            {[
              { key: 'maxExposure', label: 'Maximum Exposure', unit: '$', current: currentStatus?.totalExposure },
              { key: 'marginCallLevel', label: 'Margin Call Level', unit: '%', current: currentStatus?.marginLevel, isReverse: true },
              { key: 'stopOutLevel', label: 'Stop Out Level', unit: '%', current: 50, isReverse: true },
              { key: 'maxDrawdown', label: 'Max Drawdown', unit: '%', current: currentStatus?.currentDrawdown },
              { key: 'dailyLossLimit', label: 'Daily Loss Limit', unit: '$', current: Math.abs(currentStatus?.dailyPnL) }
            ]?.map((limit) => (
              <div key={limit?.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{limit?.label}</span>
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs font-mono ${getStatusColor(limit?.current, isEditingLimits ? tempLimits?.[limit?.key] : riskLimits?.[limit?.key], limit?.isReverse)}`}>
                      {limit?.unit === '$' ? formatCurrency(limit?.current) : `${limit?.current}${limit?.unit}`}
                    </span>
                    <span className="text-xs text-muted-foreground">/</span>
                    {isEditingLimits ? (
                      <Input
                        type="number"
                        value={tempLimits?.[limit?.key]}
                        onChange={(e) => setTempLimits(prev => ({
                          ...prev,
                          [limit?.key]: parseFloat(e?.target?.value) || 0
                        }))}
                        className="w-20 h-6 text-xs"
                      />
                    ) : (
                      <span className="text-xs font-mono text-foreground">
                        {limit?.unit === '$' ? formatCurrency(riskLimits?.[limit?.key]) : `${riskLimits?.[limit?.key]}${limit?.unit}`}
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(limit?.current, isEditingLimits ? tempLimits?.[limit?.key] : riskLimits?.[limit?.key], limit?.isReverse)}`}
                    style={{
                      width: `${Math.min(100, (limit?.current / (isEditingLimits ? tempLimits?.[limit?.key] : riskLimits?.[limit?.key])) * 100)}%`
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Quick Actions</h3>
          
          <div className="space-y-3">
            <Button
              variant="destructive"
              fullWidth
              onClick={() => setShowEmergencyDialog(true)}
              iconName="AlertTriangle"
              iconPosition="left"
              className="justify-start"
            >
              Emergency Close All Positions
            </Button>
            
            <Button
              variant="outline"
              fullWidth
              iconName="Pause"
              iconPosition="left"
              className="justify-start"
            >
              Suspend All Trading
            </Button>
            
            <Button
              variant="outline"
              fullWidth
              iconName="RefreshCw"
              iconPosition="left"
              className="justify-start"
            >
              Sync Risk Data
            </Button>
            
            <Button
              variant="outline"
              fullWidth
              iconName="FileText"
              iconPosition="left"
              className="justify-start"
            >
              Generate Risk Report
            </Button>
          </div>

          {/* Current Status Summary */}
          <div className="mt-6 p-4 bg-muted/30 rounded-lg">
            <h4 className="text-sm font-medium text-foreground mb-3">Current Status</h4>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Open Positions:</span>
                <span className="font-mono text-foreground">{currentStatus?.openPositions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Exposure:</span>
                <span className="font-mono text-foreground">{formatCurrency(currentStatus?.totalExposure)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Margin Level:</span>
                <span className={`font-mono ${getStatusColor(currentStatus?.marginLevel, riskLimits?.marginCallLevel, true)}`}>
                  {currentStatus?.marginLevel}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Daily P&L:</span>
                <span className={`font-mono ${currentStatus?.dailyPnL >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatCurrency(currentStatus?.dailyPnL)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Emergency Close Dialog */}
      {showEmergencyDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="AlertTriangle" size={24} className="text-red-500" />
              <h3 className="text-lg font-semibold text-foreground">Emergency Close All Positions</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              This action will immediately close all open positions. This cannot be undone.
              Type "CLOSE ALL" to confirm this action.
            </p>
            
            <Input
              label="Confirmation"
              placeholder="Type 'CLOSE ALL' to confirm"
              value={emergencyConfirmation}
              onChange={(e) => setEmergencyConfirmation(e?.target?.value)}
              className="mb-4"
            />
            
            <div className="flex space-x-2">
              <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  setShowEmergencyDialog(false);
                  setEmergencyConfirmation('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                fullWidth
                onClick={handleEmergencyClose}
                disabled={emergencyConfirmation?.toLowerCase() !== 'close all'}
              >
                Close All Positions
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalRiskControls;